import { DatePipe } from '@angular/common';
import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core';
import { FormBuilder, ReactiveFormsModule } from '@angular/forms';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { BlockUIModule } from '@openng/optimus-ui/blockui';
import { ButtonModule } from '@openng/optimus-ui/button';
import { CardModule } from '@openng/optimus-ui/card';
import { DatePickerModule } from '@openng/optimus-ui/datepicker';
import { InputTextModule } from '@openng/optimus-ui/inputtext';
import { PaginatorModule, PaginatorState } from '@openng/optimus-ui/paginator';
import { PanelModule } from '@openng/optimus-ui/panel';
import { SelectModule } from '@openng/optimus-ui/select';
import { firstValueFrom } from 'rxjs';
import { MOBILE_NOTE_FORM } from 'src/app/constants/common.constants';
import { ProgressSpinnerComponent } from 'src/app/core/components/progress-spinner.component';
import { SearchContainerComponent } from 'src/app/core/containers/search-container/search-container.component';
import { TabService } from 'src/app/core/services/tab.service';
import { ControllerNote } from 'src/app/model/controllerNotes/controllerNote';
import {
  MobileControllerNotesSearchFormGroup,
  MobileControllerNotesSearchRawValue,
} from 'src/app/model/controllerNotes/mobile-controller-notes-search-form';
import { NoteType } from 'src/app/model/crm/note-type';
import { AOR } from 'src/app/model/locations/aor';
import { AorApiService } from 'src/app/services/api/aor-api.service';
import { ControllerNotesApiService } from 'src/app/services/api/controller-notes-api.service';
import { NoteTypeApiService } from 'src/app/services/api/note-type-api.service';
import { BreadCrumbBuilderService } from 'src/app/services/builder/breadcrumb-builder.service';
import { ControllerNotesBuilderService } from 'src/app/services/builder/controller-notes-builder.service';
import { sortByName } from 'src/app/utils/utils';

@Component({
  selector: 'app-mobile-controller-notes-search',
  imports: [
    BlockUIModule,
    ProgressSpinnerComponent,
    PanelModule,
    DatePickerModule,
    TranslateModule,
    ReactiveFormsModule,
    ButtonModule,
    InputTextModule,
    SelectModule,
    CardModule,
    DatePipe,
    PaginatorModule,
  ],
  templateUrl: './mobile-controller-notes-search.component.html',
  styleUrl: './mobile-controller-notes-search.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class MobileControllerNotesSearchComponent
  extends SearchContainerComponent<ControllerNote>
  implements OnInit
{
  protected _translateService = inject(TranslateService);
  protected _breadCrumbService = inject(BreadCrumbBuilderService);
  protected _controllerNotesApi = inject(ControllerNotesApiService);
  protected _controllerNotesBuilder = inject(ControllerNotesBuilderService);
  protected _aorApi = inject(AorApiService);
  protected _noteTypeApi = inject(NoteTypeApiService);
  protected _fb = inject(FormBuilder);
  protected _tabService = inject(TabService);

  readonly pageLoading = signal(false);
  readonly page = signal(0);
  readonly pageSize = signal(15);
  readonly _totalRecords = signal(0);
  readonly controllerNotes = signal<ControllerNote[]>([]);
  readonly aors = signal<AOR[]>([]);
  readonly noteTypes = signal<NoteType[]>([]);
  readonly categories = computed(() => {
    const types = this.noteTypes();
    const categoryNames = new Set(
      types.flatMap((noteType) => noteType.categories.map((c) => c.name))
    );

    return Array.from(categoryNames).sort();
  });
  searchForm: MobileControllerNotesSearchFormGroup;

  constructor() {
    super();

    const tomorrow = new Date();
    tomorrow.setDate(tomorrow.getDate() + 1);
    this.searchForm = this._fb.group({
      startDate: this._fb.control(new Date(), { nonNullable: true }),
      endDate: this._fb.control(tomorrow, { nonNullable: true }),
      aor: this._fb.control<string | null>(null),
      category: this._fb.control<string | null>(null),
      noteType: this._fb.control<string | null>(null),
      facilityName: this._fb.control<string | null>(null),
      noteText: this._fb.control<string | null>(null),
    });

    this.loadDropdownData();
  }

  async ngOnInit(): Promise<void> {
    const screenName = await firstValueFrom(
      this._translateService.get('CRM.SCREEN.CONTROLLER_NOTES_SEARCH')
    );

    this._tabService.updateActiveTabLabel(screenName);
    this._breadCrumbService.resetAndAddBreadCrumb({
      label: screenName,
      routerLink: null,
    });
    this.retrieveControllerNotes();
  }

  retrieveControllerNotes(): void {
    this.pageLoading.set(true);
    const filters: MobileControllerNotesSearchRawValue =
      this.searchForm.getRawValue();
    const query =
      this._controllerNotesBuilder.buildControllerNotesMobileQuery(filters);

    this._controllerNotesApi
      .queryForControllerNotes(
        this.page() + 1,
        this.pageSize(),
        query,
        { updatedAt: 'DESC' },
        false
      )
      .subscribe({
        next: (data) => {
          this.controllerNotes.set(data.items);
          this._totalRecords.set(data.totalRecords);
          this.pageLoading.set(false);
        },
        error: () => {},
      });
  }

  clearFilters(): void {
    this.searchForm.reset();
    this.page.set(0);
    this.retrieveControllerNotes();
  }

  onControllerNoteSelected(note: ControllerNote): void {
    const newTab = this._tabService.buildNewTab(
      MOBILE_NOTE_FORM,
      true,
      undefined,
      note.id
    );
    this._tabService.setMobileTab(newTab);
  }

  onPageChange($event: PaginatorState) {
    this.page.set($event.page!);
    this.pageSize.set($event.rows!);
    this.retrieveControllerNotes();
  }

  loadDropdownData() {
    this._aorApi.getAORs(null).subscribe((data) => {
      this.aors.set(data.sort(sortByName));
    });

    this._noteTypeApi.getNoteTypes().subscribe((data) => {
      this.noteTypes.set(data.sort(sortByName));
    });
  }
}
